import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { FaAt, FaLocationDot, FaPhone } from "react-icons/fa6";
import { RiErrorWarningFill } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { useContact } from '../contexts/ContactContext';
import PageHeader from "./common/PageHeader";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const cardHover = {
  hover: {
    y: -5,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.3
    }
  }
};

const campuses = [
  { value: "lephalale", label: "Lephalale" },
  { value: "mokopane", label: "Mokopane" },
  { value: "midrand", label: "Midrand" },
  { value: "moletjie", label: "Moletjie" },
];

const Contacts = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    campus: "",
    cellMinistry: "",
    message: "",
  });
  const [submitFormStatusMessage, setFormStatusMessage] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);
  const {sendContactMessage} = useContact();
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });

    if (name === "email" || name === "phone") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try{
      const response = await sendContactMessage(formData);

      setFormSuccess(response.success);

      if (formSuccess){
        setFormStatusMessage(response.message);
        navigate('/contact');
      }
      else{
        setFormStatusMessage(response.message)
      }
    }
    catch(error){
      console.error('Contact submission error:', error);
      console.log(error);
      setErrors({
        type: error,
        message: 'Failed to send message. Please try again later.'
      });
    }
    finally {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        campus: "",
        cellMinistry: "",
        message: "",
      });
      setErrors({});
    }
  };

  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case "firstName":
      case "lastName":
        return value.trim() === ""
          ? `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`
          : "";
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? "" : "Invalid email";
      case "phone":
        const phoneRegex = /^(\+27|0)\d{9}$/;
        return phoneRegex.test(value)
          ? ""
          : "Invalid format (e.g., +27123456789 or 0123456789)";
      case "campus":
        return value === "" ? "Please select a campus" : "";
      case "cellMinistry":
        return value === "" ? "Please select yes or no" : "";
      case "message":
        return value.trim() === "" ? "Message is required" : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = validateField(name, value);
      if (error) {
        newErrors[name] = error;
      }
    });
    return newErrors;
  };

  return (
    <motion.main 
      className="pt-5 space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header Section */}
      <PageHeader 
        title="Contact Us" 
        subtitle="How can we help you today?"
        imageUrl="https://images.unsplash.com/photo-1528747045269-390fe33c19f2?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        imageAlt="Church Events"
      />

      <div className="container mx-auto px-4 py-3 max-w-7xl">
        {/* Contact Form Section */}
        <motion.section 
          className="bg-white rounded-xl shadow-lg mb-10 mt-10 p-8"
          variants={itemVariants}
          whileHover="hover"
          variants={cardHover}
        >
          <h3 className="text-2xl font-semibold mb-8 text-gray-800">Get in touch with us</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Group */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-6"
              variants={itemVariants}
            >
              <div className="w-full">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  {errors.firstName && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                  First Name:
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.firstName 
                      ? "border-red-500 focus:ring-red-200" 
                      : "border-gray-300 focus:ring-primary/50"
                  }`}
                />
                {errors.firstName && (
                  <motion.p 
                    className="text-red-500 text-xs mt-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.firstName}
                  </motion.p>
                )}
              </div>
              <div className="w-full">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  {errors.lastName && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                  Last Name:
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.lastName 
                      ? "border-red-500 focus:ring-red-200" 
                      : "border-gray-300 focus:ring-primary/50"
                  }`}
                />
                {errors.lastName && (
                  <motion.p 
                    className="text-red-500 text-xs mt-1"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.lastName}
                  </motion.p>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={itemVariants}>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {errors.phone && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                Phone Number:
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.phone 
                    ? "border-red-500 focus:ring-red-200" 
                    : "border-gray-300 focus:ring-primary/50"
                }`}
              />
              {errors.phone && (
                <motion.p 
                  className="text-red-500 text-xs mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.phone}
                </motion.p>
              )}
            </motion.div>

            <motion.div variants={itemVariants}>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {errors.email && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                Email:
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.email 
                    ? "border-red-500 focus:ring-red-200" 
                    : "border-gray-300 focus:ring-primary/50"
                }`}
                placeholder="example@email.com"
              />
              {errors.email && (
                <motion.p 
                  className="text-red-500 text-xs mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.email}
                </motion.p>
              )}
            </motion.div>

            {/* Message */}
            <motion.div variants={itemVariants}>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {errors.message && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                Share your Testimony or Prayer request with us:
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.message 
                    ? "border-red-500 focus:ring-red-200" 
                    : "border-gray-300 focus:ring-primary/50"
                }`}
              />
              {errors.message && (
                <motion.p 
                  className="text-red-500 text-xs mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.message}
                </motion.p>
              )}
            </motion.div>

            {/* Campus Selection */}
            <motion.div variants={itemVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {errors.campus && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                Which Campus do you attend?
              </label>
              <select
                id="campus"
                name="campus"
                value={formData.campus}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.campus 
                    ? "border-red-500 focus:ring-red-200" 
                    : "border-gray-300 focus:ring-primary/50"
                }`}
              >
                <option value="">Select Your Campus</option>
                {campuses.map((campus) => (
                  <option key={campus.value} value={campus.value}>
                    {campus.label}
                  </option>
                ))}
              </select>
              {errors.campus && (
                <motion.p 
                  className="text-red-500 text-xs mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.campus}
                </motion.p>
              )}
            </motion.div>

            {/* Cell Group */}
            <motion.div variants={itemVariants}>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Do you belong to any cell group?
        </label>
        <div className="flex items-center space-x-6 mb-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="cellMinistry"
              value="Yes"
              checked={formData.cellMinistry === "Yes"}
              onChange={handleChange}
              className="h-5 w-5 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-gray-700">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="cellMinistry"
              value="No"
              checked={formData.cellMinistry === "No"}
              onChange={handleChange}
              className="h-5 w-5 text-primary focus:ring-primary"
            />
            <span className="ml-2 text-gray-700">No</span>
          </label>
        </div>

        <AnimatePresence>
          {formData.cellMinistry === "Yes" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <label htmlFor="cellGroupName" className="block text-sm font-medium text-gray-700 mb-2">
                {errors.cellGroupName && <RiErrorWarningFill className="inline text-red-500 mr-1" />}
                Name of your Cell Group:
              </label>
              <input
                type="text"
                name="cellGroupName"
                id="cellGroupName"
                value={formData.cellGroupName}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.cellGroupName 
                    ? "border-red-500 focus:ring-red-200" 
                    : "border-gray-300 focus:ring-primary/50"
                }`}
                placeholder="Enter your cell group name"
              />
              {errors.cellGroupName && (
                <motion.p 
                  className="text-red-500 text-xs mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {errors.cellGroupName}
                </motion.p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

            {/* Submit Button */}
            <motion.div 
              className="text-center pt-4"
              variants={itemVariants}
            >
              <motion.button
                type="submit"
                className="px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                Submit
              </motion.button>
            </motion.div>

            <AnimatePresence>
              {submitFormStatusMessage && (
                <motion.div
                  className={`rounded-lg p-4 shadow-sm ${
                    formSuccess 
                      ? 'bg-green-100 text-green-900 border-green-300' 
                      : 'bg-red-100 text-red-900 border-red-300'
                  } border`}
                  role="alert"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {submitFormStatusMessage}
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.section>

        {/* Offices Section */}
        <motion.section 
          className="bg-white p-8 rounded-xl shadow-lg"
          variants={itemVariants}
        >
          <div className="w-full h-0.5 bg-gray-200 my-6 mx-auto max-w-2xl"></div>
          <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Our Offices</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Office Address 1 */}
            <motion.div 
              className="bg-gray-50 rounded-xl shadow-md p-6 border-l-4 border-primary"
              variants={itemVariants}
              whileHover="hover"
              variants={cardHover}
            >
              <div className="flex items-start mb-4">
                <FaLocationDot className="text-primary mt-1 mr-3 text-xl" />
                <div className="text-gray-700">
                  <h3 className="font-semibold">Lephalale Office</h3>
                  <p className="mt-1">
                    20406 Phomolng section<br />
                    Shongoane 1<br />
                    Lephalale<br />
                    0555
                  </p>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <FaPhone className="text-primary mr-3 text-xl" />
                <div className="text-gray-700">(+27) 0726298072</div>
              </div>
              <div className="flex items-center">
                <FaAt className="text-primary mr-3 text-xl" />
                <div className="text-gray-700">ExcellentLoveMinistries@gmail.com</div>
              </div>
            </motion.div>

            {/* Office Address 2 */}
            <motion.div 
              className="bg-gray-50 rounded-xl shadow-md p-6 border-l-4 border-primary"
              variants={itemVariants}
              whileHover="hover"
              variants={cardHover}
            >
              <div className="flex items-start mb-4">
                <FaLocationDot className="text-primary mt-1 mr-3 text-xl" />
                <div className="text-gray-700">
                  <h3 className="font-semibold">Mokopane Office</h3>
                  <p className="mt-1">
                    Ga-Madiba New Stands<br />
                    Behind Daata New Warehouse<br />
                    Mokopane<br />
                    0626
                  </p>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <FaPhone className="text-primary mr-3 text-xl" />
                <div className="text-gray-700">(+27) 0714187675</div>
              </div>
              <div className="flex items-center">
                <FaAt className="text-primary mr-3 text-xl" />
                <div className="text-gray-700">ExcellentLoveMinistries@gmail.com</div>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </motion.main>
  );
};

export default Contacts;